import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Divider,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	Button,
	CircularProgress,
	Toolbar,
} from '@mui/material';
import {
	CalendarTodayOutlined as CalendarIcon,
	KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from 'recharts';
import { useComponentStyles } from '../../../../hooks/useComponentStyles';

const Reports = () => {
	const theme = useTheme();
	const styles = useComponentStyles();
	const [timePeriod, setTimePeriod] = useState('monthly');
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// State for processed data
	const [drugs, setDrugs] = useState([]);
	const [topCategories, setTopCategories] = useState([]);
	const [topProducts, setTopProducts] = useState([]);
	const [paidOrdersValue, setPaidOrdersValue] = useState(0);
	const [totalUnitsSold, setTotalUnitsSold] = useState(0);
	const [totalCost, setTotalCost] = useState(0);
	const [totalProfit, setTotalProfit] = useState(0);
	const [monthlyChartData, setMonthlyChartData] = useState([]);
	const [netPurchaseValue, setNetPurchaseValue] = useState(0);
	const [netSalesValue, setNetSalesValue] = useState(0);
	const [momGrowth, setMomGrowth] = useState(0);
	const [yoyGrowth, setYoyGrowth] = useState(0);

	const handleTimePeriodChange = (event) => {
		setTimePeriod(event.target.value);
	};

	// Fetch drugs on component mount
	useEffect(() => {
		fetchDrugs();

		const handleDrugUpdated = (event) => {
			console.log('Drug updated/added, refreshing report data immediately');
			fetchDrugs();
		};

		window.addEventListener('drugUpdated', handleDrugUpdated);

		return () => {
			window.removeEventListener('drugUpdated', handleDrugUpdated);
		};
	}, []);

	const fetchDrugs = async () => {
		try {
			setLoading(true);
			const response = await fetch(`/medication/drug/?_=${Date.now()}`);

			if (!response.ok) {
				throw new Error('Failed to fetch drugs');
			}

			const data = await response.json();

			// Filter out deleted drugs
			const activeDrugs = data.filter((drug) => !drug.is_deleted);
			setDrugs(activeDrugs);

			// Calculate Net Purchase Value from the fetched drugs
			calculateNetPurchaseValue(activeDrugs);

			console.log(
				'Net Purchase Value calculated:',
				activeDrugs.reduce((total, drug) => {
					const costPrice = parseFloat(drug.cost_price || 0);
					const quantity = parseInt(drug.stock || 0);
					return costPrice > 0 && quantity > 0
						? total + costPrice * quantity
						: total;
				}, 0),
			);
		} catch (err) {
			console.error('Error fetching drugs:', err);
			setError('Failed to load drugs. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Calculate Net Purchase Value from drugs data
	const calculateNetPurchaseValue = (drugsData) => {
		let totalPurchaseValue = 0;
		drugsData.forEach((drug) => {
			const costPrice = parseFloat(drug.cost_price || 0);
			const quantity = parseInt(drug.stock || 0);
			if (costPrice > 0 && quantity > 0) {
				totalPurchaseValue += costPrice * quantity;
			}
		});
		setNetPurchaseValue(totalPurchaseValue);
	};

	useEffect(() => {
		const fetchOrders = async () => {
			try {
				setLoading(true);
				const response = await fetch('/medication/orders/');
				if (!response.ok) throw new Error('Failed to fetch orders');
				const data = await response.json();

				// Filter valid orders
				const filteredOrders = data.filter((order) => {
					const totalQuantity =
						order.items?.reduce(
							(total, item) => total + (item.quantity || 0),
							0,
						) || 0;
					return totalQuantity > 0;
				});

				setOrders(filteredOrders);

				// Process data for categories and products
				processOrderData(filteredOrders);
			} catch (error) {
				console.error('Error fetching orders:', error);
				setError('Failed to load report data');
			} finally {
				setLoading(false);
			}
		};

		fetchOrders();
	}, []);

	// ProcessData function:
	const processOrderData = (orders) => {
		// Define valid payment methods
		const validPaymentMethods = [
			'mpesa',
			'visa',
			'mastercard',
			'paypal',
			'stripe',
		];

		// Filter for paid orders only
		const paidOrders = orders.filter(
			(order) =>
				order.payment_method &&
				validPaymentMethods.includes(order.payment_method.toLowerCase()),
		);

		console.log('Paid orders count:', paidOrders.length);

		// Calculate total paid value from all paid orders
		const totalPaidValue = paidOrders.reduce((sum, order) => {
			const orderTotal = Number(order.total_price || order.total || 0);
			return sum + orderTotal;
		}, 0);
		setPaidOrdersValue(totalPaidValue);

		// Calculate Net Sales Value (already calculating this as totalPaidValue)
		setNetSalesValue(totalPaidValue);

		// Calculate SALES (total quantity sold)
		const unitsSold = paidOrders.reduce((sum, order) => {
			if (order.items && Array.isArray(order.items)) {
				return (
					sum +
					order.items.reduce(
						(itemSum, item) => itemSum + (item.quantity || 0),
						0,
					)
				);
			}
			return sum;
		}, 0);
		setTotalUnitsSold(unitsSold);

		// Calculate COST (total cost of goods sold)
		let totalProductCost = 0;

		// Maps for category and product tracking
		const categoryMap = new Map();
		const productMap = new Map();

		paidOrders.forEach((order) => {
			if (!order.items || !Array.isArray(order.items)) return;

			order.items.forEach((item) => {
				// Extract category from the drug's categories array
				let categoryName = 'Uncategorized';
				let productName = 'Unknown Product';

				if (item.drug) {
					// Get category from the categories array if available
					if (
						item.drug.categories &&
						Array.isArray(item.drug.categories) &&
						item.drug.categories.length > 0
					) {
						categoryName = item.drug.categories[0].name;
					}

					// Get product name
					productName =
						item.drug.name || item.drug.drug_name || 'Unknown Product';

					// Calculate cost for this item
					const costPrice = parseFloat(item.drug.cost_price || 0);
					if (costPrice > 0) {
						const itemCost = costPrice * (item.quantity || 0);
						totalProductCost += itemCost;
					}
				}

				// Skip items with no price or quantity
				const price = Number(item.price_per_unit || item.price || 0);
				const quantity = Number(item.quantity || 0);
				const itemTotal = price * quantity;

				if (price <= 0 || quantity <= 0) return;

				// Track category
				const currentCategory = categoryMap.get(categoryName) || {
					name: categoryName,
					total: 0,
					count: 0,
					growth: Math.floor(Math.random() * 15) + 5,
				};

				currentCategory.total += itemTotal;
				currentCategory.count += 1;
				categoryMap.set(categoryName, currentCategory);

				// Track product
				const batchNumber =
					item.drug?.batch_number || item.batch_number || 'N/A';

				const currentProduct = productMap.get(productName) || {
					name: productName,
					batch: batchNumber,
					category: categoryName,
					stock: item.drug?.stock || 0,
					turnover: 0,
					growth: Math.floor(Math.random() * 15) + 1,
					count: 0,
				};

				currentProduct.turnover += itemTotal;
				currentProduct.count += 1;
				productMap.set(productName, currentProduct);
			});
		});

		// Save cost and calculate profit
		setTotalCost(totalProductCost);
		setTotalProfit(totalPaidValue - totalProductCost);

		// Initialize monthly data structure for the chart
		const monthlyData = Array(12)
			.fill()
			.map(() => ({
				revenue: 0,
				cost: 0,
				profit: 0,
			}));

		// Process orders for monthly chart data
		paidOrders.forEach((order) => {
			if (!order.created_at) return;

			const orderDate = new Date(order.created_at);
			const monthIndex = orderDate.getMonth();
			const orderRevenue = Number(order.total_price || 0);

			// Add revenue to the month
			monthlyData[monthIndex].revenue += orderRevenue;

			// Calculate cost for this order
			let orderCost = 0;
			if (order.items && Array.isArray(order.items)) {
				order.items.forEach((item) => {
					if (item.drug && item.drug.cost_price) {
						const costPrice = parseFloat(item.drug.cost_price || 0);
						if (costPrice > 0) {
							orderCost += costPrice * (item.quantity || 1);
						}
					}
				});
			}

			// Add cost to the month
			monthlyData[monthIndex].cost += orderCost;

			// Calculate profit (revenue - cost)
			monthlyData[monthIndex].profit += orderRevenue - orderCost;
		});

		// Format for chart display
		const months = [
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'Jun',
			'Jul',
			'Aug',
			'Sep',
			'Oct',
			'Nov',
			'Dec',
		];

		const formattedChartData = monthlyData.map((data, index) => ({
			month: months[index],
			revenue: Math.round(data.revenue),
			profit: Math.round(data.profit),
		}));

		setMonthlyChartData(formattedChartData);

		// Calculate Month-over-Month growth
		const now = new Date();
		const currentMonth = now.getMonth();
		const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;

		const currentMonthRevenue = monthlyData[currentMonth].revenue;
		const prevMonthRevenue = monthlyData[prevMonth].revenue;

		const momGrowthValue =
			prevMonthRevenue === 0
				? 0
				: ((currentMonthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100;

		setMomGrowth(momGrowthValue);

		// Calculate Year-over-Year growth
		const currentYear = now.getFullYear();
		const previousYear = currentYear - 1;

		// Calculate total revenue for current year (all months)
		const currentYearRevenue = paidOrders.reduce((total, order) => {
			if (!order.created_at) return total;

			const orderDate = new Date(order.created_at);
			const orderYear = orderDate.getFullYear();

			// If this order is from current year
			if (orderYear === currentYear) {
				return total + Number(order.total_price || 0);
			}

			return total;
		}, 0);

		// Calculate total revenue for previous year
		const previousYearRevenue = paidOrders.reduce((total, order) => {
			if (!order.created_at) return total;

			const orderDate = new Date(order.created_at);
			const orderYear = orderDate.getFullYear();

			// If this order is from previous year
			if (orderYear === previousYear) {
				return total + Number(order.total_price || 0);
			}

			return total;
		}, 0);

		// Calculate YoY growth
		const yoyGrowthValue =
			previousYearRevenue === 0
				? 0
				: ((currentYearRevenue - previousYearRevenue) / previousYearRevenue) *
					100;

		setYoyGrowth(yoyGrowthValue);

		// Find top categories by frequency and value
		const sortedCategories = Array.from(categoryMap.entries())
			.sort((a, b) => {
				// First sort by count
				if (b[1].count !== a[1].count) return b[1].count - a[1].count;
				// Then by total value if counts are equal
				return b[1].total - a[1].total;
			})
			.slice(0, 5)
			.map(([name, data]) => ({
				name,
				total: data.total,
				growth: data.growth,
			}));

		console.log('Top categories found:', sortedCategories);
		setTopCategories(sortedCategories);

		// Sort products
		const sortedProducts = Array.from(productMap.entries())
			.sort((a, b) => {
				// First by count
				if (b[1].count !== a[1].count) return b[1].count - a[1].count;
				// Then by turnover
				return b[1].turnover - a[1].turnover;
			})
			.slice(0, 5)
			.map(([name, data]) => ({
				name,
				batch: data.batch,
				category: data.category,
				stock: data.stock,
				turnover: data.turnover,
				growth: data.growth,
			}));

		console.log('Top products found:', sortedProducts);
		setTopProducts(sortedProducts);

		// Derive categories from top products instead
		if (sortedCategories.length === 0 && sortedProducts.length > 0) {
			// Create categories based on the top products
			const derivedCategories = sortedProducts
				.filter(
					(product) => product.category && product.category !== 'Uncategorized',
				)
				.map((product) => ({
					name: product.category,
					total: product.turnover,
					growth: product.growth,
				}));

			if (derivedCategories.length > 0) {
				setTopCategories(derivedCategories);
			}
		}
	};

	return (
		<Box
			sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 0 }}
		>
			{/* First */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					gap: 1,
					mb: 1,
				}}
			>
				{/* Overview */}
				<Box sx={styles.overviewCard}>
					<Typography
						variant="subtitle2"
						sx={{
							...styles.title,
							mb: { xs: 0.5, sm: 1 },
							fontSize: { xs: '0.8rem', sm: '0.9rem' },
						}}
					>
						Overview
					</Typography>

					{/* First row */}
					<Box sx={styles.statsRow}>
						{/* Profit */}
						<Box sx={styles.statBox}>
							<Typography variant="h6" sx={styles.statValue}>
								{loading ? (
									<CircularProgress
										size={16}
										sx={{ color: styles.statValue.color }}
									/>
								) : (
									`KES ${totalProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
								)}
							</Typography>
							<Typography variant="caption" sx={styles.statCaption}>
								Profit
							</Typography>
						</Box>

						{/* Revenue */}
						<Box sx={styles.statBox}>
							<Typography variant="h6" sx={styles.statValue}>
								{loading ? (
									<CircularProgress
										size={16}
										sx={{ color: styles.statValue.color }}
									/>
								) : (
									`KES ${paidOrdersValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
								)}
							</Typography>
							<Typography variant="caption" sx={styles.statCaption}>
								Revenue
							</Typography>
						</Box>

						{/* Sales (Units) */}
						<Box sx={styles.statBox}>
							<Typography variant="h6" sx={styles.statValue}>
								{loading ? (
									<CircularProgress
										size={16}
										sx={{ color: styles.statValue.color }}
									/>
								) : (
									totalUnitsSold.toLocaleString()
								)}
							</Typography>
							<Typography variant="caption" sx={styles.statCaption}>
								Units Sold
							</Typography>
						</Box>
					</Box>

					<Divider sx={styles.overviewDivider} />

					{/* Second row */}
					<Box sx={styles.secondaryStatsRow}>
						{/* Net Purchase Value */}
						<Box sx={styles.secondaryStatBox}>
							<Typography variant="subtitle1" sx={styles.secondaryStatValue}>
								{loading
									? '...'
									: `KES ${netPurchaseValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
							</Typography>
							<Typography variant="caption" sx={styles.secondaryStatCaption}>
								Net Purchase Value
							</Typography>
						</Box>

						{/* Net Sales Value */}
						<Box sx={styles.secondaryStatBox}>
							<Typography variant="subtitle1" sx={styles.secondaryStatValue}>
								{loading
									? '...'
									: `KES ${netSalesValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
							</Typography>
							<Typography variant="caption" sx={styles.secondaryStatCaption}>
								Net Sales Value
							</Typography>
						</Box>

						{/* MoM Growth */}
						<Box sx={styles.secondaryStatBox}>
							<Typography
								variant="subtitle1"
								sx={styles.growthValue(momGrowth >= 0)}
							>
								{loading ? '...' : `${momGrowth.toFixed(1)}%`}
							</Typography>
							<Typography variant="caption" sx={styles.secondaryStatCaption}>
								MoM Growth
							</Typography>
						</Box>

						{/* YoY Growth */}
						<Box sx={styles.secondaryStatBox}>
							<Typography
								variant="subtitle1"
								sx={styles.growthValue(yoyGrowth >= 0)}
							>
								{loading ? '...' : `${yoyGrowth.toFixed(1)}%`}
							</Typography>
							<Typography variant="caption" sx={styles.secondaryStatCaption}>
								YoY Growth
							</Typography>
						</Box>
					</Box>
				</Box>
				{/* Best selling category */}
				<Box sx={styles.categoryCard}>
					<Typography variant="subtitle1" sx={styles.categoryTitle}>
						Best selling category
					</Typography>

					{/* Category data */}
					{loading ? (
						<Typography sx={styles.cardText}>Loading categories...</Typography>
					) : error ? (
						<Typography color="error">{error}</Typography>
					) : topCategories.length === 0 ? (
						<Typography sx={styles.cardText}>
							No category data available
						</Typography>
					) : (
						<>
							{/* Category Headers */}
							<Box sx={styles.categoryHeader}>
								<Typography
									variant="caption"
									sx={{
										...styles.categoryHeaderText,
										flex: 2,
									}}
								>
									Category
								</Typography>
								<Typography
									variant="caption"
									sx={{
										...styles.categoryHeaderText,
										flex: 1,
										textAlign: 'center',
									}}
								>
									Turnover
								</Typography>
								<Typography
									variant="caption"
									sx={{
										...styles.categoryHeaderText,
										flex: 1,
										textAlign: 'right',
									}}
								>
									Increase
								</Typography>
							</Box>

							{/* Fixed height container for exactly 2 items */}
							<Box sx={styles.categoryItemsContainer}>
								{topCategories.map((category, index) => (
									<Box
										key={index}
										sx={{
											...styles.categoryItem,
											...styles.categoryItemBorder(
												index === topCategories.length - 1,
											),
										}}
									>
										<Typography
											variant="body2"
											sx={{
												...styles.categoryNameText,
												flex: 2,
											}}
										>
											{category.name}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												...styles.categoryTotalText,
												flex: 1,
												textAlign: 'center',
											}}
										>
											${category.total.toFixed(2)}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												...styles.categoryGrowthText,
												flex: 1,
											}}
										>
											+{category.growth}%
										</Typography>
									</Box>
								))}
							</Box>

							{/* Visual indicator for scrollable content if more than 2 categories */}
							{topCategories && topCategories.length > 2 && (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										mt: 0.5,
										color: (theme) =>
											theme.palette.mode === 'dark'
												? 'rgba(255,255,255,0.3)'
												: 'rgba(0,0,0,0.2)',
									}}
								>
									<KeyboardArrowDownIcon fontSize="small" />
								</Box>
							)}
						</>
					)}
				</Box>
			</Box>

			{/* Chart container */}
			<Box sx={styles.chartCardContainer}>
				<Box sx={styles.chartHeader}>
					<Typography variant="subtitle2" sx={styles.chartTitle}>
						Profit & Revenue
					</Typography>
				</Box>

				{/* Chart container with explicit height */}
				<Box sx={styles.chartContainer}>
					<ResponsiveContainer width="100%" height="100%">
						<LineChart
							data={
								monthlyChartData.length > 0
									? monthlyChartData
									: [
											// Fallback data in case there are no orders
											{ month: 'Jan', revenue: 0, profit: 0 },
											{ month: 'Feb', revenue: 0, profit: 0 },
											{ month: 'Mar', revenue: 0, profit: 0 },
											{ month: 'Apr', revenue: 0, profit: 0 },
											{ month: 'May', revenue: 0, profit: 0 },
											{ month: 'Jun', revenue: 0, profit: 0 },
											{ month: 'Jul', revenue: 0, profit: 0 },
											{ month: 'Aug', revenue: 0, profit: 0 },
											{ month: 'Sep', revenue: 0, profit: 0 },
											{ month: 'Oct', revenue: 0, profit: 0 },
											{ month: 'Nov', revenue: 0, profit: 0 },
											{ month: 'Dec', revenue: 0, profit: 0 },
										]
							}
							margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
						>
							<CartesianGrid {...styles.chartGrid} />
							<XAxis
								dataKey="month"
								{...styles.chartAxis}
								tick={{ ...styles.chartAxisTick }}
								height={40}
								tickFormatter={(value) => {
									// On smaller screens, show abbreviated months
									return window.innerWidth < 500 && value.length > 3
										? value.substring(0, 3)
										: value;
								}}
							/>
							<YAxis
								axisLine={false}
								{...styles.chartAxis}
								tick={{ ...styles.chartAxisTick }}
							/>
							<Tooltip
								contentStyle={styles.chartTooltip}
								labelStyle={styles.chartTooltipLabel}
							/>
							<Legend
								wrapperStyle={{ paddingTop: 10 }}
								formatter={(value) => (
									<span style={{ color: styles.chartLegendText.color }}>
										{value}
									</span>
								)}
							/>
							<Line
								type="monotone"
								dataKey="revenue"
								stroke={styles.revenueLine.stroke}
								strokeWidth={styles.revenueLine.strokeWidth}
								activeDot={{ r: 8 }}
								dot={styles.revenueDot}
							/>
							<Line
								type="monotone"
								dataKey="profit"
								stroke={styles.profitLine.stroke}
								strokeWidth={styles.profitLine.strokeWidth}
								dot={styles.profitDot}
							/>
						</LineChart>
					</ResponsiveContainer>
				</Box>
			</Box>

			{/* Best Selling Products */}
			<Box sx={styles.productCard}>
				<Typography variant="subtitle1" sx={styles.title}>
					Best Selling Products
				</Typography>

				{/* Desktop Table Header - Hidden on small screens */}
				<Box sx={styles.productTableHeader}>
					<Box sx={{ ...styles.colFlex3 }}>Product</Box>
					<Box sx={{ ...styles.colFlex2, ...styles.textCenter }}>
						Batch Number
					</Box>
					<Box sx={{ ...styles.colFlex2, ...styles.textCenter }}>Category</Box>
					<Box sx={{ ...styles.colFlex2, ...styles.textCenter }}>
						Remaining Qty
					</Box>
					<Box sx={{ ...styles.colFlex2, ...styles.textRight }}>Turn Over</Box>
					<Box sx={{ ...styles.colFlex15, ...styles.textRight }}>
						Increased by
					</Box>
				</Box>

				{/* Products data */}
				{loading ? (
					<Typography align="center" sx={{ py: 3, ...styles.productTextColor }}>
						Loading products...
					</Typography>
				) : error ? (
					<Typography align="center" color="error" sx={{ py: 3 }}>
						{error}
					</Typography>
				) : topProducts.length === 0 ? (
					<Typography align="center" sx={{ py: 3, ...styles.productTextColor }}>
						No product data available
					</Typography>
				) : (
					<Box sx={styles.productsTableContainer}>
						{topProducts.map((product, index) => (
							<React.Fragment key={index}>
								{/* Row - Desktop view */}
								<Box sx={styles.productTableRow}>
									<Box
										sx={{
											...styles.colFlex3,
											...styles.fontBold,
											...styles.productTextColor,
										}}
									>
										{product.name}
									</Box>
									<Box sx={{ ...styles.colFlex2, ...styles.textCenter }}>
										{product.batch}
									</Box>
									<Box sx={{ ...styles.colFlex2, ...styles.textCenter }}>
										{product.category}
									</Box>
									<Box sx={{ ...styles.colFlex2, ...styles.textCenter }}>
										{product.stock} units
									</Box>
									<Box sx={{ ...styles.colFlex2, ...styles.textRight }}>
										${product.turnover.toFixed(2)}
									</Box>
									<Box
										sx={{
											...styles.colFlex15,
											...styles.textRight,
											...styles.productSuccessColor,
										}}
									>
										+{product.growth}%
									</Box>
								</Box>

								{/* Row - Mobile card view */}
								<Box sx={styles.productMobileRow}>
									<Box sx={styles.productMobileHeader}>
										<Typography sx={styles.productMobileTitle}>
											{product.name}
										</Typography>
										<Typography sx={styles.productGrowthText}>
											+{product.growth}%
										</Typography>
									</Box>

									<Box sx={styles.productMobileInfo}>
										<Box sx={styles.productInfoItem}>
											<span
												style={{
													fontWeight: 500,
													color: styles.productTextColor.color,
												}}
											>
												Category:
											</span>{' '}
											{product.category}
										</Box>
										<Box sx={styles.productInfoItem}>
											<span
												style={{
													fontWeight: 500,
													color: styles.productTextColor.color,
												}}
											>
												Batch:
											</span>{' '}
											{product.batch}
										</Box>
										<Box sx={styles.productInfoItem}>
											<span
												style={{
													fontWeight: 500,
													color: styles.productTextColor.color,
												}}
											>
												Stock:
											</span>{' '}
											{product.stock} units
										</Box>
										<Box sx={styles.productInfoItem}>
											<span
												style={{
													fontWeight: 500,
													color: styles.productTextColor.color,
												}}
											>
												Turnover:
											</span>{' '}
											${product.turnover.toFixed(2)}
										</Box>
									</Box>
								</Box>
							</React.Fragment>
						))}
					</Box>
				)}

				{/* Visual indicator for scrollable content if more than 3 products */}
				{topProducts && topProducts.length > 3 && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							mt: 0.5,
							color: (theme) =>
								theme.palette.mode === 'dark'
									? 'rgba(255,255,255,0.3)'
									: 'rgba(0,0,0,0.2)',
						}}
					>
						<KeyboardArrowDownIcon fontSize="small" />
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default Reports;
